import { useEffect, useRef, useState } from 'react';
import { setInvoicePromisedPaymentDate, updateInvoiceInternalNote } from '../../lib/invoices';
import { formatDate, isTodayOrFuture } from '../../lib/format';

interface InvoiceInternalNoteProps {
  invoiceId: string;
  /** Huidige interne notitie (SF Interne_Notitie__c). */
  internalNote: string;
  /** Huidige toegezegde betaaldatum als yyyy-MM-dd, of '' als leeg. */
  promisedPaymentDate: string;
  /** Openstaand bedrag van de factuur — bepaalt of een verlopen toezegging nog telt. */
  openAmount?: number;
  /** Aangeroepen na een succesvolle opslag — parent herlaadt de factuur. */
  onSaved: () => void;
}

/**
 * Interne opvolging op een factuur: toegezegde betaaldatum + vrije notitie. Beide
 * velden zijn alleen voor kantoor en verschijnen NIET op de factuur of in
 * klant-communicatie. Een toegezegde betaaldatum in de toekomst onderdrukt de
 * automatische 1e/2e betalingsherinnering (SF-flow) tot en met die datum; daarna
 * hervat de cadence zonder inhaal-herinnering. De datum staat altijd zichtbaar
 * bovenaan (eigen quick-save) en raakt de notitie niet aan.
 */
export function InvoiceInternalNote({
  invoiceId,
  internalNote,
  promisedPaymentDate,
  openAmount,
  onSaved,
}: InvoiceInternalNoteProps) {
  // Datum: altijd zichtbaar, eigen quick-save (los van de notitie).
  const [dateValue, setDateValue] = useState(promisedPaymentDate);
  const [dateSaving, setDateSaving] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);

  // Notitie: inline bewerken, zelfde patroon als voorheen.
  const [editing, setEditing] = useState(false);
  const [noteDraft, setNoteDraft] = useState(internalNote);
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!dateSaving) setDateValue(promisedPaymentDate);
  }, [promisedPaymentDate, dateSaving]);

  useEffect(() => {
    if (!editing) setNoteDraft(internalNote);
  }, [internalNote, editing]);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
      const end = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(end, end);
    }
  }, [editing]);

  const saveDate = async (next: string) => {
    const normalized = next || '';
    setDateValue(normalized);
    if (normalized === (promisedPaymentDate || '')) return;
    setDateSaving(true);
    setDateError(null);
    const result = await setInvoicePromisedPaymentDate(invoiceId, normalized || null);
    setDateSaving(false);
    if (result.ok) {
      onSaved();
    } else {
      setDateValue(promisedPaymentDate);
      setDateError(result.error);
    }
  };

  const startEdit = () => {
    setNoteDraft(internalNote);
    setNoteError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setNoteDraft(internalNote);
    setNoteError(null);
  };

  const saveNote = async () => {
    if (noteDraft.trim() === internalNote.trim()) {
      setEditing(false);
      return;
    }
    setNoteSaving(true);
    setNoteError(null);
    // Stuur de huidige (persistente) datum mee zodat notitie-opslaan hem niet wist.
    const result = await updateInvoiceInternalNote(invoiceId, {
      internalNote: noteDraft,
      promisedPaymentDate,
    });
    setNoteSaving(false);
    if (result.ok) {
      setEditing(false);
      onSaved();
    } else {
      setNoteError(result.error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    } else if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      void saveNote();
    }
  };

  const hasNote = internalNote.trim().length > 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
      <div className="mb-3">
        <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          Interne opvolging
        </h2>
        <p className="text-[11px] text-slate-400">Alleen voor kantoor, niet zichtbaar voor de klant</p>
      </div>

      {/* Toegezegde betaaldatum — altijd zichtbaar, onderdrukt de herinneringen */}
      <div className="mb-4 pb-4 border-b border-slate-100">
        <label className="block text-[11px] font-medium text-slate-500 mb-1">
          Toegezegde betaaldatum <span className="text-slate-400">(optioneel)</span>
        </label>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={dateValue}
            onChange={(e) => void saveDate(e.target.value)}
            disabled={dateSaving}
            className="text-sm text-slate-900 border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50"
          />
          {dateValue ? (
            <button
              type="button"
              onClick={() => void saveDate('')}
              disabled={dateSaving}
              className="text-xs text-slate-500 hover:text-slate-700 disabled:opacity-50"
            >
              Wissen
            </button>
          ) : null}
          {dateSaving ? <span className="text-[11px] text-slate-400">Opslaan…</span> : null}
        </div>
        <div className="mt-1.5">
          <DatePauseStatus date={dateValue} openAmount={openAmount} />
        </div>
        {dateError ? <p className="text-[11px] text-amber-700 mt-1">{dateError}</p> : null}
      </div>

      {/* Interne notitie */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[11px] font-medium text-slate-500">Notitie</label>
          {!editing ? (
            <button
              type="button"
              onClick={startEdit}
              className="text-xs text-indigo-600 hover:underline"
            >
              {hasNote ? 'Bewerken' : 'Toevoegen'}
            </button>
          ) : null}
        </div>
        {editing ? (
          <div className="space-y-2">
            <textarea
              ref={textareaRef}
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={noteSaving}
              rows={3}
              className="w-full text-sm text-slate-900 border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y"
              placeholder="Bijv. klant mailt 3/6: betaalt 14 juni. Bellen als niet binnen."
            />
            {noteError ? <p className="text-xs text-amber-700">{noteError}</p> : null}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={saveNote}
                disabled={noteSaving}
                className="text-xs font-medium px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {noteSaving ? 'Opslaan…' : 'Opslaan'}
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                disabled={noteSaving}
                className="text-xs text-slate-600 hover:text-slate-900 disabled:opacity-50"
              >
                Annuleren
              </button>
              <span className="text-[10px] text-slate-400 ml-auto">⌘+Enter / Esc</span>
            </div>
          </div>
        ) : hasNote ? (
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{internalNote}</p>
        ) : (
          <p className="text-sm text-slate-400 italic">Geen interne notitie</p>
        )}
      </div>
    </div>
  );
}

/**
 * Statuslijn onder de datum: geen toezegging, gepauzeerd tot [datum], of
 * verlopen (alleen relevant zolang de factuur nog openstaat).
 */
function DatePauseStatus({ date, openAmount }: { date: string; openAmount?: number }) {
  if (!date) {
    return (
      <p className="text-[11px] text-slate-400">Geen toezegging, herinneringen lopen normaal.</p>
    );
  }
  if (isTodayOrFuture(date)) {
    return (
      <p className="text-[11px] font-medium text-emerald-700">
        Betaalherinneringen gepauzeerd tot {formatDate(date)}.
      </p>
    );
  }
  const stillOpen = openAmount === undefined || openAmount > 0.005;
  if (stillOpen) {
    return (
      <p className="text-[11px] font-medium text-red-700">
        Toezegging verlopen op {formatDate(date)}.
      </p>
    );
  }
  return (
    <p className="text-[11px] text-slate-400">Toegezegde betaaldatum was {formatDate(date)}.</p>
  );
}
